import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import axios from 'axios';
import { useMediaQuery } from 'react-responsive';
import {
  Box, Container, Typography, Paper, Button, Alert, CircularProgress, Chip,
  LinearProgress, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Tooltip, Dialog, DialogTitle, DialogContent,
  InputAdornment, TextField, IconButton as MuiIconButton,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DownloadIcon from '@mui/icons-material/Download';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import TableChartIcon from '@mui/icons-material/TableChart';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';

import { useUserAuth } from '../../context/UserAuthContext';
import Header from '../../components/Header/Header';
import Logo from '../../components/Logo/Logo';
import CompanyCellEditor from './CompanyCellEditor';
import FpaXlsxPanel from './FpaXlsxPanel';
import {
  parseCsvFile, mapAndNormalizeRows, validateRow, findDuplicates, rowsToCsv,
} from './forecastUtils';

ModuleRegistry.registerModules([AllCommunityModule]);

// ── Company lookup dialog ─────────────────────────────────────────────────────

function CompanyLookupDialog({ open, onClose, companies, loading }) {
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(null);

  const filtered = companies
    .filter(c => {
      const q = search.toLowerCase();
      return c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q);
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  function handleCopy(id) {
    navigator.clipboard.writeText(id).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 1500);
    });
  }

  function handleExport() {
    const rows = [['Name', 'Company ID'], ...filtered.map(c => [c.name, c.id])];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'axial-companies.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>Company ID Lookup</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Export to CSV">
            <span>
              <MuiIconButton size="small" onClick={handleExport} disabled={filtered.length === 0}>
                <DownloadIcon fontSize="small" />
              </MuiIconButton>
            </span>
          </Tooltip>
          <MuiIconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></MuiIconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name or ID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoFocus
          sx={{ mb: 1.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Company ID</TableCell>
                  <TableCell sx={{ width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="body2" color="text.secondary" py={2}>No matches</Typography>
                    </TableCell>
                  </TableRow>
                ) : filtered.map(c => (
                  <TableRow key={c.id} hover>
                    <TableCell>{c.name}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>
                      {c.id}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={copied === c.id ? 'Copied!' : 'Copy ID'}>
                        <MuiIconButton size="small" onClick={() => handleCopy(c.id)}>
                          <ContentCopyIcon fontSize="small" sx={{ color: copied === c.id ? 'success.main' : 'action.active' }} />
                        </MuiIconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {filtered.length} of {companies.length} companies
        </Typography>
      </DialogContent>
    </Dialog>
  );
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_ROWS = 10000;

function fmtCurrency(val) {
  const n = Number(val);
  if (isNaN(n)) return val;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ── Delete button cell renderer ───────────────────────────────────────────────

function DeleteCellRenderer({ data, context }) {
  if (context.disabled) return null;
  return (
    <button
      onClick={() => context.onDeleteRow(data._id)}
      title="Remove row"
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#c62828', fontSize: 15, padding: '0 4px', lineHeight: 1,
      }}
    >
      ✕
    </button>
  );
}

// ── Results panel ─────────────────────────────────────────────────────────────

function ResultsPanel({ rows, results, summary, companiesById, onRetryFailed }) {
  const failedIndices = results.reduce((acc, r, i) => {
    if (r?.status === 'error') acc.push(i);
    return acc;
  }, []);

  return (
    <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>Upload Results</Typography>

      {summary && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            icon={<CheckCircleIcon />}
            label={`${summary.created} Created`}
            color="success"
            size="small"
          />
          <Chip
            icon={<CheckCircleIcon />}
            label={`${summary.updated} Updated`}
            color="primary"
            size="small"
          />
          {summary.failed > 0 && (
            <Chip
              icon={<ErrorIcon />}
              label={`${summary.failed} Failed`}
              color="error"
              size="small"
            />
          )}
        </Box>
      )}

      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 60 }}>Line</TableCell>
              <TableCell>Company</TableCell>
              <TableCell sx={{ width: 110 }}>Date</TableCell>
              <TableCell sx={{ width: 110 }}>Total Sales</TableCell>
              <TableCell sx={{ width: 110 }}>Status</TableCell>
              <TableCell>Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => {
              const result = results[i];
              const company = companiesById[String(row.company_id || '').toLowerCase()];
              const isOk = result?.status === 'ok';
              const isErr = result?.status === 'error';
              const pending = !result;
              return (
                <TableRow
                  key={row._id}
                  sx={{ bgcolor: isErr ? alpha('#c62828', 0.04) : isOk ? alpha('#2e7d32', 0.04) : undefined }}
                >
                  <TableCell sx={{ color: '#999', fontSize: 11 }}>{row._lineNo}</TableCell>
                  <TableCell>{company ? company.name : row.company_id}</TableCell>
                  <TableCell>{row.date}</TableCell>
                  <TableCell>{fmtCurrency(row.total_sales)}</TableCell>
                  <TableCell>
                    {pending && <CircularProgress size={12} />}
                    {isOk && (
                      <Chip
                        label={result.operation || 'ok'}
                        size="small"
                        color="success"
                        sx={{ height: 20, fontSize: 10 }}
                      />
                    )}
                    {isErr && (
                      <Chip
                        label="failed"
                        size="small"
                        color="error"
                        sx={{ height: 20, fontSize: 10 }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 11, color: isErr ? '#c62828' : 'text.secondary' }}>
                    {result?.message || ''}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {summary && failedIndices.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<ReplayIcon />}
            onClick={() => onRetryFailed(failedIndices)}
          >
            Retry {failedIndices.length} Failed Row{failedIndices.length !== 1 ? 's' : ''}
          </Button>
        </Box>
      )}
    </Paper>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ForecastUploadPage() {
  const { user: firebaseUser } = useUserAuth();
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });

  const gridRef = useRef();
  const abortRef = useRef(null); // for SSE cancellation

  const [mode, setMode] = useState('xlsx'); // 'xlsx' (FP&A unified flow) | 'csv' (per-row Axial)
  const [companies, setCompanies] = useState([]);
  const [companiesError, setCompaniesError] = useState(null);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [lookupOpen, setLookupOpen] = useState(false);

  const [filename, setFilename] = useState(null);
  const [rowData, setRowData] = useState(null); // set once on parse; grid owns state after that
  const [cellErrors, setCellErrors] = useState({}); // { [_id]: { [field]: errorMsg } }
  const [rowWarnings, setRowWarnings] = useState({}); // { [_id]: string[] }
  const [duplicates, setDuplicates] = useState([]);
  const [parseError, setParseError] = useState(null);

  // Upload flow
  const [phase, setPhase] = useState('idle'); // idle | parsed | uploading | done
  const [uploadRows, setUploadRows] = useState([]); // rows sent to server
  const [results, setResults] = useState([]); // per-row results from SSE
  const [summary, setSummary] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);

  const companiesById = useMemo(() => {
    const m = {};
    companies.forEach(c => { m[c.id.toLowerCase()] = c; });
    return m;
  }, [companies]);

  const companiesSet = useMemo(
    () => new Set(companies.map(c => c.id.toLowerCase())),
    [companies]
  );

  const getToken = useCallback(async () => {
    if (!firebaseUser?.getIdToken) return null;
    return firebaseUser.getIdToken(true);
  }, [firebaseUser]);

  // Load companies on mount
  useEffect(() => {
    (async () => {
      setLoadingCompanies(true);
      try {
        const token = await getToken();
        if (!token) return;
        const { data } = await axios.get('/hd-api/forecast/companies', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCompanies(data);
      } catch (e) {
        setCompaniesError(e.response?.data?.error || 'Failed to connect to Axial — company list unavailable.');
      } finally {
        setLoadingCompanies(false);
      }
    })();
  }, [getToken]);

  // ── CSV drop/parse ───────────────────────────────────────────────────────────

  const onDrop = useCallback(async (accepted, rejected) => {
    if (rejected.length) {
      setParseError(rejected[0].errors?.[0]?.message || 'Invalid file.');
      return;
    }
    const file = accepted[0];
    if (file.size > MAX_FILE_SIZE) {
      setParseError('File is too large (max 5 MB).');
      return;
    }

    setParseError(null);
    setPhase('idle');
    setSummary(null);
    setResults([]);
    setUploadError(null);

    try {
      const result = await parseCsvFile(file);
      const { rows, error } = mapAndNormalizeRows(result);
      if (error) { setParseError(error); return; }
      if (rows.length === 0) { setParseError('No data rows found in the CSV.'); return; }
      if (rows.length > MAX_ROWS) { setParseError(`CSV has ${rows.length} rows (max ${MAX_ROWS}).`); return; }

      // Compute initial validation
      const errors = {};
      const warnings = {};
      for (const row of rows) {
        const { errors: e, warnings: w } = validateRow(row, companiesSet);
        if (Object.keys(e).length) errors[row._id] = e;
        if (w.length) warnings[row._id] = w;
      }
      setCellErrors(errors);
      setRowWarnings(warnings);
      setDuplicates(findDuplicates(rows));
      setFilename(file.name);
      setRowData(rows);
      setPhase('parsed');
    } catch (e) {
      setParseError(e.message || 'Failed to parse CSV.');
    }
  }, [companiesSet]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'], 'application/vnd.ms-excel': ['.csv'] },
    maxFiles: 1,
  });

  // ── Cell validation on edit ──────────────────────────────────────────────────

  const onCellValueChanged = useCallback(({ data }) => {
    const { errors: e, warnings: w } = validateRow(data, companiesSet);

    setCellErrors(prev => {
      const next = { ...prev };
      if (Object.keys(e).length) next[data._id] = e;
      else delete next[data._id];
      return next;
    });
    setRowWarnings(prev => {
      const next = { ...prev };
      if (w.length) next[data._id] = w;
      else delete next[data._id];
      return next;
    });

    // Re-check duplicates across all rows
    const allRows = [];
    gridRef.current?.api.forEachNode(n => allRows.push(n.data));
    setDuplicates(findDuplicates(allRows));
  }, [companiesSet]);

  const onDeleteRow = useCallback((_id) => {
    gridRef.current?.api.applyTransaction({
      remove: [{ _id }],
    });
    setCellErrors(prev => { const next = { ...prev }; delete next[_id]; return next; });
    setRowWarnings(prev => { const next = { ...prev }; delete next[_id]; return next; });
    // Re-check duplicates
    const allRows = [];
    gridRef.current?.api.forEachNode(n => { if (n.data._id !== _id) allRows.push(n.data); });
    setDuplicates(findDuplicates(allRows));
  }, []);

  // ── Column definitions ───────────────────────────────────────────────────────

  const columnDefs = useMemo(() => {
    const errClass = ({ data, colDef }) => !!(cellErrors[data._id]?.[colDef.field]);
    const errTip = ({ data, colDef }) => cellErrors[data._id]?.[colDef.field] || '';

    return [
      {
        headerName: 'Line',
        field: '_lineNo',
        width: 68,
        editable: false,
        pinned: 'left',
        cellStyle: { color: '#999', fontSize: 11 },
      },
      {
        headerName: 'Company',
        field: 'company_id',
        flex: 2,
        minWidth: 180,
        cellEditor: CompanyCellEditor,
        cellEditorParams: {},
        valueFormatter: ({ value }) => {
          if (!value) return '';
          return companiesById[value.toLowerCase()]?.name || value;
        },
        cellClassRules: { 'fc-cell-error': errClass },
        tooltipValueGetter: errTip,
      },
      {
        headerName: 'Date',
        field: 'date',
        width: 130,
        cellClassRules: { 'fc-cell-error': errClass },
        tooltipValueGetter: errTip,
      },
      {
        headerName: 'Core Sales',
        field: 'core_sales',
        width: 130,
        valueFormatter: ({ value }) => value ? fmtCurrency(value) : '',
        cellClassRules: { 'fc-cell-error': errClass },
        tooltipValueGetter: errTip,
      },
      {
        headerName: 'Catering Sales',
        field: 'catering_sales',
        width: 140,
        valueFormatter: ({ value }) => value ? fmtCurrency(value) : '',
        cellClassRules: { 'fc-cell-error': errClass },
        tooltipValueGetter: errTip,
      },
      {
        headerName: 'Total Sales',
        field: 'total_sales',
        width: 130,
        valueFormatter: ({ value }) => value ? fmtCurrency(value) : '',
        cellClassRules: { 'fc-cell-error': errClass },
        tooltipValueGetter: errTip,
      },
      {
        headerName: '',
        field: '_del',
        width: 48,
        editable: false,
        sortable: false,
        suppressHeaderMenuButton: true,
        cellRenderer: DeleteCellRenderer,
      },
    ];
  }, [companiesById, cellErrors]);

  const defaultColDef = useMemo(() => ({
    editable: phase === 'parsed',
    resizable: true,
    sortable: true,
    tooltipShowDelay: 300,
  }), [phase]);

  const gridContext = useMemo(() => ({
    companies,
    disabled: phase !== 'parsed',
    onDeleteRow,
  }), [companies, phase, onDeleteRow]);

  // ── Upload ───────────────────────────────────────────────────────────────────

  const totalErrors = Object.keys(cellErrors).length;
  const totalWarnings = Object.keys(rowWarnings).length + duplicates.length;

  function collectGridRows() {
    const rows = [];
    gridRef.current?.api.forEachNode(n => rows.push(n.data));
    return rows;
  }

  async function submitRows(rows) {
    const token = await getToken();
    if (!token) return;

    const rowsPayload = rows.map(r => ({
      company_id: r.company_id,
      date: r.date,
      core_sales: r.core_sales,
      catering_sales: r.catering_sales,
      total_sales: r.total_sales,
    }));

    setUploadRows(rows);
    setResults(new Array(rows.length).fill(null));
    setSummary(null);
    setUploadError(null);
    setProgress(0);
    setPhase('uploading');

    let completed = 0;

    try {
      const response = await fetch('/hd-api/forecast/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rows: rowsPayload, filename }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${response.status}`);
      }

      const xJobId = response.headers.get('X-Job-Id');
      if (xJobId) setJobId(xJobId);

      // Store reader for cancellation
      const reader = response.body.getReader();
      abortRef.current = { reader };

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let event;
          try { event = JSON.parse(line.slice(6)); } catch { continue; }

          if (event.type === 'row') {
            completed++;
            setProgress(Math.round((completed / rows.length) * 100));
            setResults(prev => {
              const next = [...prev];
              next[event.index] = { status: event.status, operation: event.operation, message: event.message };
              return next;
            });
          } else if (event.type === 'done') {
            setSummary(event.summary);
            setJobId(null);
          }
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        setUploadError(e.message || 'Upload failed.');
      }
    } finally {
      abortRef.current = null;
      setPhase('done');
    }
  }

  function handleSubmit() {
    const rows = collectGridRows();
    submitRows(rows);
  }

  async function handleCancel() {
    if (jobId) {
      const token = await getToken();
      if (token) {
        axios.delete(`/hd-api/forecast/upload/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});
      }
    }
    abortRef.current?.reader?.cancel();
  }

  function handleRetryFailed(failedIndices) {
    const rows = failedIndices.map(i => uploadRows[i]);
    submitRows(rows);
  }

  function handleDownloadCsv() {
    const rows = collectGridRows();
    const csv = rowsToCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename ? filename.replace(/\.csv$/i, '-edited.csv') : 'forecast.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  const hasGrid = rowData && (phase === 'parsed' || phase === 'uploading' || phase === 'done');
  const canSubmit = phase === 'parsed' && totalErrors === 0;

  return (
    <div>
      <Header isTabletOrMobile={isTabletOrMobile} />
      <Container maxWidth="xl">
        <Logo isTabletOrMobile={isTabletOrMobile} />

        <Box sx={{ mt: 2 }}>
          {/* Page header */}
          <Paper
            elevation={0}
            sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: 2 }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderLeft: (theme) => `4px solid ${theme.palette.secondary.main}`,
                px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                  borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <TableChartIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Lato', color: 'text.primary' }}>
                  FP&A Forecast Upload
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {mode === 'xlsx'
                    ? 'Drop the FP&A xlsx — runs the Snowflake load and Axial upload from one file'
                    : 'Upload a per-row forecast CSV, review and edit, then push to Axial'}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }} />
              <ToggleButtonGroup
                value={mode}
                exclusive
                size="small"
                onChange={(_, v) => v && setMode(v)}
                sx={{ mr: 1 }}
              >
                <ToggleButton value="xlsx">FP&A xlsx</ToggleButton>
                <ToggleButton value="csv">CSV</ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SearchIcon />}
                onClick={() => setLookupOpen(true)}
                disabled={loadingCompanies || companies.length === 0}
              >
                Company Lookup
              </Button>
            </Box>
          </Paper>

          {/* Axial connectivity banner */}
          {companiesError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {companiesError} Company validation is disabled.
            </Alert>
          )}

          {mode === 'xlsx' && (
            <FpaXlsxPanel getToken={getToken} />
          )}

          {mode === 'csv' && (<>
          {/* Drop zone */}
          <Paper
            {...getRootProps()}
            variant="outlined"
            sx={{
              p: 4,
              mb: 2,
              textAlign: 'center',
              cursor: 'pointer',
              borderStyle: 'dashed',
              borderColor: isDragActive ? 'secondary.main' : 'divider',
              bgcolor: isDragActive ? (t) => alpha(t.palette.secondary.main, 0.04) : 'background.paper',
              transition: 'all .15s',
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" fontWeight={500}>
              {isDragActive ? 'Drop the CSV here' : filename ? `Loaded: ${filename}` : 'Drop a CSV file here, or click to browse'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Columns: company_id, date, core_sales, catering_sales, total_sales · Max 5 MB / 10,000 rows
            </Typography>
          </Paper>

          {parseError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setParseError(null)}>
              {parseError}
            </Alert>
          )}

          {/* Duplicate warning */}
          {duplicates.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {duplicates.length} duplicate (company + date) pair{duplicates.length !== 1 ? 's' : ''} found:{' '}
              {duplicates.slice(0, 3).map(d => `lines ${d.otherLineNo} & ${d.lineNo}`).join(', ')}
              {duplicates.length > 3 ? ` and ${duplicates.length - 3} more` : ''}. Axial last-write-wins.
            </Alert>
          )}

          {/* AG Grid */}
          {hasGrid && (
            <Paper variant="outlined" sx={{ mb: 2 }}>
              {/* Toolbar */}
              <Box sx={{
                px: 2, py: 1.5,
                display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                borderBottom: '1px solid', borderColor: 'divider',
              }}>
                <Typography variant="subtitle2" sx={{ mr: 1 }}>
                  {loadingCompanies && <CircularProgress size={12} sx={{ mr: 0.5 }} />}
                  {rowData?.length} row{rowData?.length !== 1 ? 's' : ''}
                </Typography>

                {totalErrors > 0 && (
                  <Chip
                    icon={<ErrorIcon />}
                    label={`${totalErrors} error${totalErrors !== 1 ? 's' : ''}`}
                    color="error"
                    size="small"
                    variant="outlined"
                  />
                )}
                {totalWarnings > 0 && (
                  <Chip
                    icon={<WarningAmberIcon />}
                    label={`${totalWarnings} warning${totalWarnings !== 1 ? 's' : ''}`}
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                )}
                {totalErrors === 0 && totalWarnings === 0 && phase === 'parsed' && (
                  <Chip icon={<CheckCircleIcon />} label="All rows valid" color="success" size="small" variant="outlined" />
                )}

                <Box sx={{ flex: 1 }} />

                {phase === 'parsed' && (
                  <Tooltip title="Download current grid as CSV">
                    <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadCsv}>
                      Export CSV
                    </Button>
                  </Tooltip>
                )}

                {phase === 'uploading' && (
                  <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />} onClick={handleCancel}>
                    Cancel
                  </Button>
                )}

                {phase === 'parsed' && (
                  <Tooltip title={totalErrors > 0 ? 'Fix errors before uploading' : ''}>
                    <span>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                      >
                        Upload to Axial
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>

              {/* Progress bar */}
              {phase === 'uploading' && (
                <LinearProgress variant="determinate" value={progress} sx={{ height: 3 }} />
              )}

              {/* Warning rows (cell-level, shown above grid) */}
              {Object.keys(rowWarnings).length > 0 && phase === 'parsed' && (
                <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: alpha('#f57c00', 0.04) }}>
                  {Object.entries(rowWarnings).slice(0, 5).map(([id, warns]) => (
                    <Typography key={id} variant="caption" display="block" color="warning.main">
                      Line {rowData.find(r => r._id === Number(id))?._lineNo}: {warns.join('; ')}
                    </Typography>
                  ))}
                  {Object.keys(rowWarnings).length > 5 && (
                    <Typography variant="caption" color="text.secondary">
                      …and {Object.keys(rowWarnings).length - 5} more warnings
                    </Typography>
                  )}
                </Box>
              )}

              <div
                className="ag-theme-alpine"
                style={{ height: Math.min(Math.max((rowData?.length || 0) * 42 + 48, 200), 520) }}
              >
                <AgGridReact
                  ref={gridRef}
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  context={gridContext}
                  onCellValueChanged={onCellValueChanged}
                  getRowId={({ data }) => String(data._id)}
                  tooltipShowDelay={300}
                  suppressMovableColumns
                  suppressRowClickSelection
                />
              </div>

              <style>{`
                .fc-cell-error.ag-cell { box-shadow: inset 0 0 0 2px #c62828 !important; }
              `}</style>
            </Paper>
          )}

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>
          )}

          {/* Results panel */}
          {phase === 'done' && (
            <ResultsPanel
              rows={uploadRows}
              results={results}
              summary={summary}
              companiesById={companiesById}
              onRetryFailed={handleRetryFailed}
            />
          )}
          </>)}
        </Box>
      </Container>

      <CompanyLookupDialog
        open={lookupOpen}
        onClose={() => setLookupOpen(false)}
        companies={companies}
        loading={loadingCompanies}
      />
    </div>
  );
}
